import { Grid, Paper } from "@mui/material";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { SubmitHandler } from "react-hook-form";
import { useNavigate } from "react-router";
import { apiUrl } from "../../api/core/apiUrl";
import { PageContent } from "../../components/core/PageContent/PageContent";
import { PageHeader } from "../../components/core/PageHeader/PageHeader";
import {
  BoardButton,
  BoardButtonInputs,
} from "../../types/app/boardButton.types";
import { Board } from "../../types/app/board.types";
import { dictionary } from "../../utils/core/dictionary";
import { BoardButtonForm } from "./BoardButtonForm";
import http from "../../api/core/api";
import { paths } from "../../utils/core/routes";
import { useParams } from "react-router-dom";
import { useBoardPermission } from "../../hooks/useBoardPermission";
import { Loader } from "../../components/core/Loader/Loader";

export function BoardButtonEdit() {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { id, parentId } = useParams();

  const [board, setBoard] = useState<Board | null>(null);
  const [defaultValues, setDefaultValues] = useState<BoardButtonInputs | null>(
    null
  );
  const { canManageButtons } = useBoardPermission(
    null,
    board?.isOwnedByCurrentUser
  );

  useEffect(() => {
    const fetchBoard = async () => {
      const response = await http.apiGet({
        url: `${apiUrl.BOARD_ITEM}?id=${parentId}`,
      });
      setBoard(response?.data);
    };
    const fetchButton = async () => {
      const response = await http.apiGet({
        url: `${apiUrl.BOARD_BUTTON_ITEM}?id=${id}`,
      });
      const data = response?.data as BoardButton | undefined;
      if (data) {
        setDefaultValues({
          id: data.id,
          boardId: data.boardId,
          label: data.label,
          url: data.url,
          style: data.style,
          order: data.order,
          openInNewTab: data.openInNewTab,
          tooltip: data.tooltip ?? "",
        });
      }
    };
    fetchBoard();
    fetchButton();
  }, [id, parentId]);

  useEffect(() => {
    if (board && !canManageButtons) {
      navigate(`${paths.BOARD_EDIT}/${Number(parentId)}`);
    }
  }, [board, canManageButtons, navigate, parentId]);

  const convertDataFromInputToRequest = (data: BoardButtonInputs) => ({
    id: data.id,
    boardId: data.boardId,
    label: data.label,
    url: data.url,
    style: data.style,
    order: Number(data.order) || 0,
    openInNewTab: !!data.openInNewTab,
    tooltip: data.tooltip || null,
  });

  const handleOnSubmit: SubmitHandler<BoardButtonInputs> = async (
    values: BoardButtonInputs
  ) => {
    try {
      let data = convertDataFromInputToRequest(values);
      const response = await http.apiPut({
        url: apiUrl.BOARD_BUTTON_PUT,
        data: data,
      });
      if (response?.status === 200) {
        enqueueSnackbar(dictionary.BOARD_BUTTON_EDITED, {
          variant: "success",
        });
        navigate(`${paths.BOARD_EDIT}/${Number(parentId)}`);
      }
    } catch (err) {
      enqueueSnackbar(`${err}`, { variant: "error" });
    }
  };

  if (!board || !defaultValues) return <Loader />;
  return (
    <>
      <PageHeader title={dictionary.BOARD_BUTTON_EDIT} />
      <PageContent>
        <Grid container spacing={1}>
          <Grid item xs={12} md={6} lg={4}>
            <Paper>
              <BoardButtonForm
                onSubmit={handleOnSubmit}
                defaultValues={defaultValues}
                onBack={() =>
                  navigate(`${paths.BOARD_EDIT}/${Number(parentId)}`)
                }
                readOnly={!canManageButtons}
              />
            </Paper>
          </Grid>
        </Grid>
      </PageContent>
    </>
  );
}
